import { useEffect, useState } from 'react';
import { preventivi } from '../../api/preventivi';
import type {
  DettaglioPreventivo,
  EsitoConferma,
  PropostaMailOfferta,
} from '../../api/preventivi';
import type { ComeStampare } from '../stampa/StampaOfferta';
import { anagrafiche } from '../../api/acquisti';
import type { AnagraficaMago } from '../../api/acquisti';
import { formattaImporto, oggiIso } from '../../lib/formato';
import { usePuo } from '../sessione/ContestoSessione';
import { apri } from '../../lib/ancora';
import { SceltaPerCommessa } from './SceltaPerCommessa';
import type { SceltaCommessa } from './SceltaPerCommessa';

/*  Dove sta il preventivo, e cosa gli si può far fare adesso.

    Il ciclo è quello vero di un'offerta: si redige, esce, e il cliente risponde sì o no.
    Ogni passaggio chiude qualcosa, e questa è la ragione per cui i comandi sono bottoni
    separati e non un elenco a discesa «stato»: cambiare uno stato scegliendolo da una
    lista fa sembrare che si stia correggendo un campo, mentre si sta mandando
    un'offerta a un cliente o impegnando l'azienda su un lavoro.

    Tre cose che vale la pena sapere prima di premere.

    L'INVIO congela l'anagrafica del cliente. Da quel momento il documento non si tocca
    più: per cambiarlo si apre una revisione, e resta traccia di quello che il cliente ha
    ricevuto. È anche il momento in cui la stampa comincia a leggere la fotografia invece
    di Mago, quindi ristampare un'offerta di marzo dà lo stesso foglio di marzo.

    La CONFERMA fa nascere la commessa. Non e' un cambio di stato: e' il momento in cui
    il lavoro esiste, con un numero su cui la squadra segnera' le ore e un budget preso
    dai conti del preventivo. Premuta due volte non ne crea due — il servizio se ne
    accorge e restituisce quella che c'e' gia'.

    La REVISIONE è come si corregge un'offerta già uscita, ed è una COPIA: nasce un
    documento nuovo con lo stesso numero e la revisione successiva — 26-0006/1 — e quello
    vecchio resta esattamente com'era. L'offerta che il cliente ha in mano deve restare
    leggibile e ristampabile: se telefona citando il 26-0006, quel documento deve esserci
    ancora. Dopo la revisione si lavora sulla copia, e la scheda ci si sposta da sola.

    Su un CONFERMATO la revisione si può fare, ma solo per chi può riaprirlo: il confermato
    resta confermato con la sua commessa, e confermare anche la revisione ne farebbe
    nascere una seconda sullo stesso lavoro.
*/
export function CicloPreventivo({
  preventivo: p,
  onCambiato,
}: {
  preventivo: DettaglioPreventivo;
  onCambiato: (aggiornato: DettaglioPreventivo) => void;
}) {
  const puo = usePuo();
  const puoRedigere = puo('RedigiPreventivi');
  const puoConfermare = puo('EmettiOrdini');
  const puoRiaprire = puo('RiapriPreventiviConfermati');

  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<EsitoConferma | null>(null);
  const [confermaAperta, setConfermaAperta] = useState(false);
  const [ordine, setOrdine] = useState('');
  const [dataOrdine, setDataOrdine] = useState(oggiIso);

  /*  Cosa di questa offerta va in commessa. Null è «tutta», ed è quello che resta finché
      nessuno tocca niente: la conferma manda null e passa dalla strada di sempre.  */
  const [scelta, setScelta] = useState<SceltaCommessa | null>(null);

  /*  LA COPIA si chiede in una maschera e non si fa al volo: il cliente e il nome
      interno sono due decisioni, e una copia muta sarebbe un secondo documento
      identico al primo che dopo non si distingue piu' in elenco.  */
  const [copiaAperta, setCopiaAperta] = useState(false);
  const [frase, setFrase] = useState('');
  const [risultati, setRisultati] = useState<AnagraficaMago[]>([]);
  const [cliente, setCliente] = useState<AnagraficaMago | null>(null);
  const [nomeCopia, setNomeCopia] = useState('');

  /*  Mezzo secondo di attesa come nelle altre ricerche: le anagrafiche stanno su un
      altro database, e una query per lettera digitata sono dieci query per un nome.  */
  useEffect(() => {
    const cercata = frase.trim();
    if (cercata.length < 2) {
      setRisultati([]);
      return;
    }

    let annullato = false;
    const attesa = setTimeout(() => {
      anagrafiche
        .clienti(cercata)
        .then((r) => !annullato && setRisultati(r))
        .catch((e: Error) => !annullato && setErrore(e.message));
    }, 500);

    return () => {
      annullato = true;
      clearTimeout(attesa);
    };
  }, [frase]);

  /*  La maschera si apre gia' sul cliente di adesso e su un nome che dice da dove
      arriva. Sono due proposte, non due vincoli: la copia per un altro cliente si fa
      cambiando il primo campo, ed e' il caso per cui il tasto esiste.  */
  const apriCopia = () => {
    setCliente({
      codiceMago: p.codiceClienteMago,
      denominazione: p.cliente ?? p.codiceClienteMago,
      citta: null,
      provincia: null,
      attiva: true,
      codiceClassificazione: p.codiceCategoria,
      classificazione: null,
    });
    setNomeCopia(`Copia di ${p.descrizioneInterna}`.slice(0, 400));
    setFrase('');
    setRisultati([]);
    setErrore(null);
    setCopiaAperta(true);
  };

  const duplica = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (cliente === null || nomeCopia.trim() === '') return;

    setCopiaAperta(false);
    esegui(
      preventivi.duplica(p.id, {
        codiceClienteMago: cliente.codiceMago,
        descrizioneInterna: nomeCopia.trim(),
      }),
    );
  };

  const esegui = (azione: Promise<DettaglioPreventivo>) => {
    setInCorso(true);
    setErrore(null);
    setEsito(null);

    azione
      .then(onCambiato)
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const conferma = (evento: React.FormEvent) => {
    evento.preventDefault();
    setInCorso(true);
    setErrore(null);

    preventivi
      .conferma(p.id, {
        numeroOrdineCliente: ordine.trim() === '' ? null : ordine.trim(),
        dataOrdine: ordine.trim() === '' ? null : dataOrdine,
        //  Null vuol dire «tutta l'offerta», che è il caso di quasi ogni conferma: da lì
        //  passa la strada di sempre e i conti restano quelli.
        righeInCommessa: scelta?.righe ?? null,
        quoteSpese: scelta?.quote ?? null,
      })
      .then((risposta) => {
        setEsito(risposta);
        setConfermaAperta(false);
        onCambiato(risposta.preventivo);
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const inRedazione = p.stato === 'Bozza' || p.stato === 'InRedazione';
  const inviato = p.stato === 'Inviato';
  const confermato = p.stato === 'Confermato';

  return (
    <section className="ciclo-preventivo">
      <h2>Stato</h2>

      <p className={`stato ${p.stato.toLowerCase()}`}>
        <strong>{ETICHETTE[p.stato] ?? p.stato}</strong>
        {p.revisione > 0 && (
          <span className="revisione">
            revisione {p.revisione}
            {/*  Da quale documento nasce. Non e' decorazione: davanti a un 26-0006/2 si
                 vuole poter tornare al /1 e vedere cosa e' cambiato.  */}
            {p.revisioneDiId && <em> · copia della precedente</em>}
          </span>
        )}
      </p>

      {/*  PRESO A META', DETTO SUBITO.

           Un'offerta da 10.000 di cui il cliente ha preso un capitolo da 4.000 resta
           scritta 10.000 dappertutto — in elenco, in stampa, nel totale della scheda —
           perché quello è il documento che il cliente ha in mano. Senza questa riga
           l'unico posto dove comparirebbe il 4.000 è il budget della commessa, e fra sei
           mesi la differenza fra i due numeri sembra un errore invece che una decisione.
           Chiesto da Andrea il 30/09.

           I due importi vicini e non uno solo: «in commessa 4.000» da sola non dice
           niente se non si ha sott'occhio da cosa è stata ricavata.  */}
      {p.prezzoInCommessa !== null && (
        <p className="in-parte">
          Il cliente ha preso <strong>una parte</strong> dell’offerta: offerti{' '}
          {formattaImporto(p.conti.prezzo)}, in commessa{' '}
          <strong>{formattaImporto(p.prezzoInCommessa)}</strong>. Il preventivo non è
          cambiato: è quello che è andato in produzione a essere più piccolo.
        </p>
      )}

      {p.commessaId !== null && (
        <p className="commessa">
          {'Commessa '}
          <button
            type="button"
            className="collegamento"
            onClick={() => apri('commesse', p.commessaId!)}
          >
            {p.numeroCommessa ?? 'senza numero'}
          </button>
          {' — è lì che vanno le ore e il materiale.'}
        </p>
      )}

      {errore !== null && <p className="errore">{errore}</p>}

      {esito !== null && (
        <p className={esito.commessaCreata ? 'nato' : 'nota'}>
          {esito.commessaCreata
            ? `Nata la commessa ${esito.numeroCommessa}: da adesso le ore e il materiale vanno lì.`
            : `La commessa ${esito.numeroCommessa} c'era già su questo lavoro: resta quella, con il budget che aveva.`}
        </p>
      )}

      {confermaAperta ? (
        <form className="modulo-conferma" onSubmit={conferma}>

          {/*  COSA DI QUESTA OFFERTA SI VA A FARE, prima del numero d'ordine perché è la
               decisione che cambia i soldi: il numero si copia da una mail, questa si
               guarda. Chiuso finché non ci sono opzionali da vedere.  */}
          <SceltaPerCommessa preventivo={p} onCambiata={setScelta} />

          {/*  FACOLTATIVI dal 15/10/2026: il lavoro accettato a voce si conferma lo
               stesso. Se c'e', il numero d'ordine e' quello che finira' sulla fattura.  */}
          <div className="ordine-cliente">
            <label>
              Numero dell'ordine del cliente <em className="motivo">(se c'è)</em>
              <input
                value={ordine}
                onChange={(e) => setOrdine(e.target.value)}
                placeholder="ODA 2026/417"
              />
            </label>

            <label>
              Data dell'ordine
              <input
                type="date"
                value={dataOrdine}
                onChange={(e) => setDataOrdine(e.target.value)}
                disabled={ordine.trim() === ''}
              />
            </label>
          </div>

          <div className="comandi">
            <button type="submit" className="primario" disabled={inCorso}>
              {inCorso ? 'Conferma…' : 'Conferma e apri la commessa'}
            </button>
            <button type="button" onClick={() => setConfermaAperta(false)}>
              Annulla
            </button>
          </div>
        </form>
      ) : (
        <div className="comandi">
          {inviato && puoConfermare && (
            <button type="button" className="primario" onClick={() => setConfermaAperta(true)}>
              Il cliente ha accettato
            </button>
          )}

          {inviato && puoRedigere && (
            <button
              type="button"
              disabled={inCorso}
              onClick={() => esegui(preventivi.nonAggiudicato(p.id))}
            >
              Persa
            </button>
          )}

          {/*  LA RIAPERTURA di un confermato, e solo a chi puo'.

               Il confermato e' bloccato ed e' giusto: e' l'offerta che il cliente ha
               accettato, e da li' nasce la commessa. Ma «bloccato per sempre» non regge
               davanti al caso vero, l'errore di battitura scoperto il giorno dopo, e
               senza una via d'uscita si finisce per annullare e rifare — perdendo il
               numero, lo storico e il legame con la commessa.

               Torna INVIATO e non in redazione: il documento e' uscito e il cliente ce
               l'ha in mano. Da li' si apre una revisione, cosi' il cliente riceve un
               documento nuovo invece di scoprire che quello vecchio e' cambiato.  */}
          {confermato && puoRiaprire && (
            <button
              type="button"
              disabled={inCorso}
              title="Torna «inviato»: da lì si apre una revisione"
              onClick={() => esegui(preventivi.riapriConfermato(p.id))}
            >
              Riapri
            </button>
          )}

          {/*  La revisione si offre anche su un'offerta gia' inviata: e' proprio il caso
               in cui serve — il cliente ha chiesto una modifica, e l'offerta di prima
               deve restare leggibile com'era.  */}
          {!inRedazione && puoRedigere && (
            <button
              type="button"
              disabled={inCorso}
              title="Ne fa una copia: questa resta com'è"
              onClick={() => esegui(preventivi.apriRevisione(p.id))}
            >
              Apri una revisione
            </button>
          )}

          {/*  LA COPIA si offre in QUALUNQUE stato, persa compresa: rifare a un altro
               cliente un'offerta che non e' andata e' esattamente il caso d'uso, ed e'
               anche il modo di farsi dei modelli — un impianto tipo scritto una volta
               e riusato.  */}
          {puoRedigere && (
            <button
              type="button"
              disabled={inCorso}
              title="Un documento nuovo con dentro lo stesso lavoro, anche per un altro cliente"
              onClick={apriCopia}
            >
              Duplica
            </button>
          )}
        </div>
      )}

      {copiaAperta && (
        <form className="modulo-copia" onSubmit={duplica}>
          <p className="nota">
            Nasce un'offerta nuova con un numero suo, con dentro righe, spese e testi di
            questa. Cantiere, chilometri e date restano da compilare, e i prezzi sono
            quelli di adesso: per riportarli ai listini di oggi c'è «Sblocca e rileggi i
            listini».
          </p>

          <label className="campo-blocco">
            <span>Cliente della copia</span>
            {cliente ? (
              <p className="dettaglio">
                <strong>{cliente.denominazione}</strong> · {cliente.codiceMago}
                <button type="button" className="rimuovi" onClick={() => setCliente(null)}>
                  cambia
                </button>
              </p>
            ) : (
              <input
                type="search"
                placeholder="Cerca cliente in Mago…"
                value={frase}
                onChange={(e) => setFrase(e.target.value)}
              />
            )}
          </label>

          {!cliente && risultati.length > 0 && (
            <ul className="risultati">
              {risultati.map((c) => (
                <li key={c.codiceMago}>
                  <button
                    type="button"
                    onClick={() => {
                      setCliente(c);
                      setFrase('');
                      setRisultati([]);
                    }}
                  >
                    <strong>{c.denominazione}</strong>
                    <span>
                      {c.codiceMago}
                      {c.citta && ` · ${c.citta}`}
                      {!c.attiva && ' · non attivo in Mago'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label>
            Descrizione interna
            <input
              type="text"
              value={nomeCopia}
              onChange={(e) => setNomeCopia(e.target.value)}
              maxLength={400}
              required
              placeholder="Il nome con cui ritroverai questa copia"
            />
          </label>

          <div className="comandi">
            <button
              type="submit"
              className="primario"
              disabled={inCorso || cliente === null || nomeCopia.trim() === ''}
            >
              {inCorso ? 'Copia…' : 'Crea la copia'}
            </button>
            <button type="button" onClick={() => setCopiaAperta(false)}>
              Annulla
            </button>
          </div>
        </form>
      )}

      {confermato && !puoRiaprire && (
        <p className="nota">
          Confermato: si legge e basta. Riaprirlo è dell’ufficio — è l’offerta che il
          cliente ha accettato, e da lì è nata la commessa.
        </p>
      )}

      {inviato && !puoConfermare && (
        <p className="nota">
          La conferma è di chi può emettere ordini: fa nascere una commessa e impegna
          l'azienda su un lavoro.
        </p>
      )}

      {p.stato === 'NonAggiudicato' && (
        <p className="nota">
          Chiuso senza commessa. Resta leggibile: è metà di quello che serve a sapere
          quante offerte si vincono.
        </p>
      )}
    </section>
  );
}

/*  IL BOTTONE CHE MANDA L'OFFERTA, e sta di fianco a quello che scarica il Word.

    Chiesto da Andrea il 15/09/2026: «il tasto manda offerta spostiamolo sotto di fianco
    al pulsante che fa scaricare il word».

    Il motivo è che quei due bottoni sono un gesto solo. Non si manda un'offerta che non
    si è vista: si scarica il documento, lo si guarda, e se va bene si manda. Stando in
    cima, il tasto dell'invio si incontrava PRIMA di aver composto la stampa, cioè nel
    punto in cui l'unica risposta giusta è «non ancora» — e l'invio congela l'anagrafica
    e blocca il documento, quindi è il bottone che meno di tutti conviene incontrare per
    sbaglio.

    Ha uno stato suo, minuscolo, invece di ereditarlo dal pannello dello stato: adesso
    vive in un'altra scheda, e un errore d'invio va letto dove si è premuto, non a due
    schermate di distanza.
*/
export function BottoneInvio({
  preventivo: p,
  onCambiato,
  come,
}: {
  preventivo: DettaglioPreventivo;
  onCambiato: (aggiornato: DettaglioPreventivo) => void;
  /*  Come stampare l'allegato della mail: le scelte della stampa qui accanto, cosi' il
      cliente riceve quello che si e' appena guardato. Senza, si stampa con tutto.  */
  come?: ComeStampare;
}) {
  const puoRedigere = usePuo()('RedigiPreventivi');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /*  LA MAIL, dal 15/10/2026. Il bottone apre la proposta — destinatario dal cliente,
      oggetto con il numero, un testo corto — e la si legge prima di mandarla: una mail
      partita non si richiama. «Segna come inviata» resta per chi l'offerta la consegna a
      mano o la manda da Outlook, ed e' quello che il bottone faceva prima.  */
  const [proposta, setProposta] = useState<PropostaMailOfferta | null>(null);
  const [a, setA] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [testo, setTesto] = useState('');

  //  Si manda solo quello che è ancora in mano nostra. Da «inviato» in poi il comando
  //  sparisce: rimandare non è un invio, è una revisione, e ha un bottone suo.
  const inRedazione = p.stato === 'Bozza' || p.stato === 'InRedazione';

  if (!inRedazione) return null;

  if (!puoRedigere)
    return (
      <p className="nota">
        Per mandare l'offerta serve il permesso di redigere i preventivi.
      </p>
    );

  const preparaMail = () => {
    setInCorso(true);
    setErrore(null);

    preventivi
      .mailOfferta(p.id)
      .then((letta) => {
        setProposta(letta);
        setA(letta.a ?? '');
        setOggetto(letta.oggetto);
        setTesto(letta.testo);
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const esegui = (azione: Promise<DettaglioPreventivo>) => {
    setInCorso(true);
    setErrore(null);

    azione
      .then((aggiornato) => {
        setProposta(null);
        onCambiato(aggiornato);
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const mandaMail = (evento: React.FormEvent) => {
    evento.preventDefault();
    esegui(
      preventivi.inviaPerMail(p.id, {
        a: a.trim(),
        oggetto: oggetto.trim(),
        testo,
        mostraPrezzi: come?.mostraPrezzi ?? true,
        prezziSoloSuiTotali: come?.prezziSoloSuiTotali ?? false,
        mostraDettaglioArticoli: come?.mostraDettaglioArticoli ?? true,
        mostraRedattore: come?.mostraRedattore ?? true,
        scavalcaPrezziDoppi: come?.scavalcaPrezziDoppi ?? false,
      }),
    );
  };

  if (proposta === null)
    return (
      <>
        <button
          type="button"
          className="primario invio"
          disabled={inCorso}
          title="Prepara la mail al cliente con l'offerta allegata"
          onClick={preparaMail}
        >
          {inCorso ? 'Preparo…' : "Manda l'offerta"}
        </button>
        {errore !== null && <p className="errore">{errore}</p>}
      </>
    );

  const prezziDetti = !(come?.mostraPrezzi ?? true)
    ? 'senza prezzi'
    : (come?.prezziSoloSuiTotali ?? false)
      ? 'con i prezzi solo sui totali'
      : 'con tutti i prezzi';

  return (
    <form className="modulo-mail" onSubmit={mandaMail}>
      <h3>Mail al cliente</h3>

      {!proposta.postaCollegata ? (
        <p className="nota">
          {proposta.perchePostaNo ?? 'La posta non è configurata.'} L'offerta si può solo
          segnare come inviata, e mandare da Outlook col file scaricato qui sopra.
        </p>
      ) : (
        <>
          <label>
            A
            <input
              type="email"
              value={a}
              onChange={(e) => setA(e.target.value)}
              placeholder="indirizzo del cliente"
              required
            />
          </label>
          {proposta.a === null && (
            <p className="nota">
              Il cliente in Mago non ha un indirizzo di posta: scrivilo qui, e conviene
              aggiungerlo anche in anagrafica.
            </p>
          )}
          <label>
            Oggetto
            <input value={oggetto} onChange={(e) => setOggetto(e.target.value)} required />
          </label>
          <label>
            Testo
            <textarea rows={9} value={testo} onChange={(e) => setTesto(e.target.value)} required />
          </label>
          <p className="nota">
            Allegato: l'offerta in {proposta.allegatoPdf ? 'PDF' : 'Word'}, {prezziDetti}, come
            impostata qui sopra.{' '}
            {proposta.rispondiA
              ? `Le risposte arrivano a ${proposta.rispondiA}.`
              : 'La tua utenza non ha un indirizzo: le risposte arriveranno alla casella di servizio.'}
          </p>
        </>
      )}

      <div className="comandi">
        {proposta.postaCollegata && (
          <button type="submit" className="primario" disabled={inCorso}>
            {inCorso ? 'Invio…' : 'Manda la mail'}
          </button>
        )}
        <button
          type="button"
          disabled={inCorso}
          title="Congela l'anagrafica e blocca il documento, senza mandare niente"
          onClick={() => esegui(preventivi.invia(p.id))}
        >
          Segna come inviata, senza mail
        </button>
        <button type="button" className="collegamento" onClick={() => setProposta(null)}>
          annulla
        </button>
      </div>

      {errore !== null && <p className="errore">{errore}</p>}
    </form>
  );
}

/*  IL CONTROLLO DEL SECONDO ADDETTO.

    Chiesto da Andrea il 28/09/2026: «redattore dovrebbe essere chi compila il preventivo
    e poi ci dovrebbe essere un secondo addetto che lo controlla».

    Sta accanto al bottone che manda l'offerta perche' e' li' che la domanda si pone —
    prima di premere «Manda», qualcuno l'ha riletta? — e non e' una transizione di stato:
    il documento dopo il controllo e' lo stesso di prima, con una firma in piu'.

    NON IMPEDISCE L'INVIO. Si e' scelto di dirlo e non di bloccarlo: un blocco su
    un'offerta da mandare entro le cinque si aggira dandosi la password a vicenda, e a
    quel punto la firma non dice piu' niente. Se un domani serve davvero, e' una riga in
    `Preventivo.Invia`.

    La firma CADE alla prima modifica, e questo bottone lo dice prima che succeda: chi
    rilegge e poi corregge una virgola deve sapere che sta cancellando quello che ha
    appena fatto.  */
export function BottoneControllo({
  preventivo: p,
  onCambiato,
}: {
  preventivo: DettaglioPreventivo;
  onCambiato: (aggiornato: DettaglioPreventivo) => void;
}) {
  const puoRedigere = usePuo()('RedigiPreventivi');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const inRedazione = p.stato === 'Bozza' || p.stato === 'InRedazione';

  /*  Su un'offerta gia' uscita non si offre il controllo, ma se e' stata controllata lo
      si dice lo stesso: la firma e' parte di quello che il cliente ha ricevuto.  */
  if (!inRedazione)
    return p.controllatoDa === null ? null : <p className="nota">{firmata(p)}.</p>;

  if (p.controllatoDa !== null)
    return (
      <p className="controllata">
        {firmata(p)}. La firma cade se qualcuno tocca ancora il documento.
      </p>
    );

  if (!puoRedigere) return null;

  const controlla = () => {
    setInCorso(true);
    setErrore(null);

    preventivi
      .controlla(p.id)
      .then(onCambiato)
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  return (
    <>
      <button
        type="button"
        className="controllo"
        disabled={inCorso}
        title="Segna che l'hai riletta. Chi l'ha scritta non può, se non è responsabile dei preventivi"
        onClick={controlla}
      >
        {inCorso ? 'Controllo…' : "L'ho riletta"}
      </button>
      {errore !== null && <p className="errore">{errore}</p>}
    </>
  );
}

/*  «Riletta da Giulia Bianchi il 28/09/2026». La data senza l'ora: a distanza di un mese
    interessa il giorno, e l'ora allungherebbe una riga che sta accanto a un bottone.  */
function firmata(p: DettaglioPreventivo): string {
  const quando = p.controllatoIl === null ? null : new Date(p.controllatoIl);

  return quando === null || Number.isNaN(quando.getTime())
    ? `Riletta da ${p.controllatoDa}`
    : `Riletta da ${p.controllatoDa} il ${quando.toLocaleDateString('it-IT')}`;
}

const ETICHETTE: Record<string, string> = {
  Bozza: 'Bozza',
  InRedazione: 'In redazione',
  Inviato: 'Inviato al cliente',
  Confermato: 'Confermato',
  NonAggiudicato: 'Non aggiudicato',
  Annullato: 'Annullato',
};
